import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Camera {
    id: string;
    name: string;
    operator: string;
    order: number;
}

interface CameraCardProps {
    camera: Camera;
    onDelete: () => void;
    onUpdate: (name: string, operator: string) => void;
}

export const CameraCard: React.FC<CameraCardProps> = ({
    camera,
    onDelete,
    onUpdate,
}) => {
    const { t } = useTranslation();

    const [edits, setEdits] = useState<{ name?: string; operator?: string }>({});

    const nameVal = edits.name !== undefined ? edits.name : camera.name;
    const opVal = edits.operator !== undefined ? edits.operator : (camera.operator || '');

    const handleChange = (field: 'name' | 'operator', value: string) => {
        setEdits(prev => ({ ...prev, [field]: value }));
    };

    const handleBlur = () => {
        if (!edits.name && !edits.operator) return;

        const newName = edits.name !== undefined ? edits.name : camera.name;
        const newOp = edits.operator !== undefined ? edits.operator : camera.operator;

        if (newName.trim()) {
            onUpdate(newName.toUpperCase(), newOp);
        }

        setEdits({});
    };

    return (
        <div data-testid={`camera-card-${camera.id}`} style={{ background: 'var(--color-panel-bg)', borderRadius: 'var(--border-radius-md)', border: '2px solid #ffb400', padding: '10px', boxShadow: 'var(--shadow-card)' }}>
            <div data-testid={`camera-header-${camera.id}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', alignItems: 'center' }}>
                <input
                    data-testid={`camera-name-input-${camera.id}`}
                    value={nameVal}
                    onChange={e => handleChange('name', e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
                    style={{ background: 'transparent', border: 'none', color: 'var(--color-text-primary)', fontWeight: 'bold', fontSize: '0.9rem', width: '100%' }}
                />
                <button data-testid={`camera-delete-${camera.id}`} onClick={onDelete} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '1rem' }}>×</button>
            </div>
            <div data-testid={`camera-operator-row-${camera.id}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--color-bg-base)', padding: '4px 8px', borderRadius: '4px' }}>
                <span style={{ fontSize: '1rem' }}>🎥</span>
                <input
                    data-testid={`camera-operator-input-${camera.id}`}
                    value={opVal}
                    onChange={e => handleChange('operator', e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
                    placeholder={t('controller.tags.unknownOperator')}
                    style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', width: '100%', fontSize: '0.8rem' }}
                />
            </div>
        </div>
    );
};
